/**
 * Memoized selectors for MyCreations component
 * 
 * These selectors fix the inefficient state calculations issue by using
 * reselect to memoize expensive filtering operations.
 */

import { createSelector } from 'reselect';

// Base selectors - these extract raw data from state
const getMyCreations = (state) => state.creation.my_creations || [];
const getActiveTab = (state) => state.creation.activeTab;
const getReports = (state) => state.creation.reports || [];
const getLoading = (state) => state.creation.loading;
const getLoadingMore = (state) => state.creation.loadingMore;
const getHasMoreCreations = (state) => state.creation.hasMoreCreations;
const getCurrentPage = (state) => state.creation.currentPage;
const getUserId = (state) => state.auth?.user?.user_id;

// Memoized selector for reported creation IDs
const getReportedCreationIds = createSelector(
  [getReports],
  (reports) => {
    return reports
      .filter(report => report.status === 'under review' || report.status === 'accepted')
      .map(report => report.creation_id);
  }
);

// Memoized selector for non-reported creations
const getNonReportedCreations = createSelector(
  [getMyCreations, getReportedCreationIds],
  (my_creations, reportedCreationIds) => {
    return my_creations.filter(creation =>
      !reportedCreationIds.includes(creation.creation_id)
    );
  }
);

// Main memoized selector for filtered creations
export const getFilteredCreations = createSelector(
  [getNonReportedCreations, getActiveTab],
  (nonReportedCreations, activeTab) => {
    return nonReportedCreations.filter(creation => {
      if (activeTab === 'all' || activeTab === 'All' || !activeTab) return true;
      return creation.category?.toLowerCase() === activeTab.toLowerCase();
    });
  }
);

// Memoized selector for all component state
export const getMyCreationsState = createSelector(
  [
    getFilteredCreations,
    getActiveTab,
    getUserId,
    getLoading,
    getLoadingMore,
    getHasMoreCreations,
    getCurrentPage
  ],
  (
    filteredCreations,
    activeTab,
    user_id,
    loading,
    loadingMore,
    hasMoreCreations,
    currentPage
  ) => ({
    filteredCreations,
    activeTab,
    user_id,
    loading,
    loadingMore,
    hasMoreCreations,
    currentPage
  })
);

// Export individual selectors for flexibility
export {
  getMyCreations,
  getActiveTab,
  getReports,
  getLoading,
  getLoadingMore,
  getHasMoreCreations,
  getCurrentPage,
  getUserId,
  getReportedCreationIds,
  getNonReportedCreations
};